import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

// Images des 7 étapes des soins du cordon ombilical - Photos réalistes
const CORDON_STEPS = [
  {
    id: 1,
    title: "Étape 1 : Se laver les mains",
    description: "Lavage soigneux des mains au savon pendant 30 secondes minimum AVANT le soin",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/f30a26ffaf6dc07884fa02545717e34828daf3aea97284ce89245e24d52bf6d9.png"
  },
  {
    id: 2,
    title: "Étape 2 : Préparer le matériel",
    description: "Compresses stériles, antiseptique si prescrit, plan de travail propre",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/2c0e2d0c1846fe666930496c78a3e422eb1cd6a9048927f7973b533ba99a312d.png"
  },
  {
    id: 3,
    title: "Étape 3 : Nettoyer la base du cordon",
    description: "Nettoyer délicatement autour de la base du cordon avec une compresse propre",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/fbd68582af4ca45fe01d42e62608bb74b931f268ef3f7fe1ef8a0d868e1e74a9.png"
  },
  {
    id: 4,
    title: "Étape 4 : Soulever le cordon",
    description: "Soulever doucement le cordon pour nettoyer en dessous, sans tirer",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/19fc1248bab8d34a7248db085c31655de980bdc0f725614dd92b32faefd1f037.png"
  },
  {
    id: 5,
    title: "Étape 5 : Observer et surveiller",
    description: "Vérifier l'aspect : sec, propre, sans rougeur, sans odeur, sans écoulement",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/340666201fb24d25705ca1a3e88cd7efe405544110e07be7ba7b747032144b89.png"
  },
  {
    id: 6,
    title: "Étape 6 : Replier la couche",
    description: "Replier le haut de la couche sous le cordon pour le garder au sec",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/f372f84a4e789b4227c36ddd1174b57c993c59030808168c60240eca675958e4.png"
  },
  {
    id: 7,
    title: "Étape 7 : Transmissions",
    description: "Noter l'aspect du cordon et signaler toute anomalie (rougeur, odeur, écoulement)",
    image: "https://static.prod-images.emergentagent.com/jobs/258d18fd-cb23-4690-a110-1da253b34772/images/8ee410bae1d7c5a80791116ba74dd52f0cc99aaa9dfbbfa9d2e1b6da500f243c.png"
  }
];

// Logo APDR complet
const APDR_LOGO_FULL = "https://customer-assets.emergentagent.com/job_0bdb7109-8029-4d81-a486-71beb721288a/artifacts/o3wx8hx1_logo%20apdr%20formation.png";

const CordonCarousel = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Musique douce de fond
  const BACKGROUND_MUSIC = "https://cdn.pixabay.com/audio/2022/10/25/audio_850edcfd29.mp3";

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % CORDON_STEPS.length);
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      if (isPlaying && !isMuted) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isMuted]);

  const handlePrev = () => {
    setCurrentStep((prev) => (prev - 1 + CORDON_STEPS.length) % CORDON_STEPS.length);
  };

  const handleNext = () => {
    setCurrentStep((prev) => (prev + 1) % CORDON_STEPS.length);
  };

  const step = CORDON_STEPS[currentStep];

  // Couleur thématique - Vert menthe pour les soins médicaux
  const themeColor = '#059669';
  const themeColorLight = '#A7F3D0';
  const themeColorDark = '#047857';

  return (
    <div 
      className="relative rounded-2xl overflow-hidden shadow-2xl border-2"
      style={{ minHeight: '480px', borderColor: themeColorLight }}
      data-testid="cordon-carousel"
    >
      <audio ref={audioRef} loop>
        <source src={BACKGROUND_MUSIC} type="audio/mpeg" />
      </audio>

      {/* Fond avec logo APDR */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${APDR_LOGO_FULL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.08
        }}
      />

      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(209,250,229,0.88) 100%)`
        }}
      />

      {/* Header */}
      <div 
        className="relative z-10 text-white p-4"
        style={{ background: `linear-gradient(to right, ${themeColor}, ${themeColorDark})` }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white p-1 shadow-lg">
              <img src={APDR_LOGO_FULL} alt="APDR" className="w-full h-full object-contain" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Formation APDR de la VAE</h3>
              <p className="text-emerald-100 text-sm">Soins du cordon ombilical - 7 étapes</p>
            </div>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
            {currentStep + 1} / {CORDON_STEPS.length}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="relative z-10 p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          {/* Image */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <img 
                src={step.image} 
                alt={step.title}
                className="w-72 h-72 object-cover rounded-2xl shadow-xl border-4 border-white"
                key={step.id}
                style={{ animation: 'fadeIn 0.5s ease-in-out' }}
              />
              <div 
                className="absolute -top-3 -left-3 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl shadow-lg border-2 border-white"
                style={{ background: `linear-gradient(to bottom right, ${themeColor}, ${themeColorDark})` }}
              >
                {step.id}
              </div>
            </div>
          </div>

          {/* Texte */}
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3" style={{ color: themeColor }}>{step.title}</h2>
            <p className="text-gray-700 text-lg mb-6 bg-white/60 p-3 rounded-lg">{step.description}</p>

            {/* Indicateurs */}
            <div className="flex gap-2 justify-center md:justify-start flex-wrap">
              {CORDON_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`h-3 rounded-full transition-all duration-300`}
                  style={{
                    backgroundColor: index === currentStep ? themeColor : themeColorLight,
                    width: index === currentStep ? '2rem' : '0.75rem'
                  }}
                  data-testid={`cordon-carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="relative z-10 bg-white/90 backdrop-blur-sm p-4 flex items-center justify-between border-t" style={{ borderColor: themeColorLight }}>
        <button
          onClick={handlePrev}
          className="flex items-center gap-2 px-4 py-2 rounded-full transition-all font-medium"
          style={{ backgroundColor: `${themeColorLight}40`, color: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorLight}
          onMouseLeave={(e) => e.target.style.backgroundColor = `${themeColorLight}40`}
          data-testid="cordon-carousel-prev"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg`}
            style={{
              backgroundColor: isPlaying ? themeColor : 'white',
              color: isPlaying ? 'white' : themeColor,
              border: isPlaying ? 'none' : `2px solid ${themeColor}`
            }}
            data-testid="cordon-carousel-play"
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
          </button>

          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all`}
            style={{
              backgroundColor: isMuted ? '#E5E7EB' : `${themeColorLight}40`,
              color: isMuted ? '#6B7280' : themeColor
            }}
            data-testid="cordon-carousel-mute"
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        <button
          onClick={handleNext}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-full transition-all font-medium"
          style={{ backgroundColor: themeColor }}
          onMouseEnter={(e) => e.target.style.backgroundColor = themeColorDark}
          onMouseLeave={(e) => e.target.style.backgroundColor = themeColor}
          data-testid="cordon-carousel-next"
        >
          Suivant
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Barre de progression */}
      <div className="relative z-10 h-2" style={{ backgroundColor: themeColorLight }}>
        <div 
          className="h-full transition-all duration-300"
          style={{ 
            background: `linear-gradient(to right, ${themeColorLight}, ${themeColor})`,
            width: `${((currentStep + 1) / CORDON_STEPS.length) * 100}%` 
          }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default CordonCarousel;
